import React, { useEffect, useState } from "react";
import Image from "next/image";
import { useTranslation } from "next-i18next";
import { getImageName } from "helpers";
import closeIcon from "/public/images/ion_close.svg";
import nextIcon from "/public/images/next-arrow.svg";
import prevIcon from "/public/images/prev-arrow.svg";
import CarouselImage from "components/customImages/CarouselImage";
import StoreLogo from "components/customImages/StoreLogo";
import { CiZoomIn, CiZoomOut } from "react-icons/ci";

type CarouselPropsType = {
  enableSwipe?: boolean;
  showNavigationButton?: boolean;
  enableZooming?: boolean;
  images: any;
  closeCarousel: () => void;
  storeImage?: string;
  selectedImageIndex?: number;
  setSelectedImageIndex?: (currentImageIndex: number) => void;
};
const THRESHOLD_SWIPE_DISTANCE = 50;
const ZOOM_IN_LIMIT = 1.4;
const ZOOM_OUT_LIMIT = 1;
const ZOOM_STEP = 0.2;

function Carousel({
  images,
  selectedImageIndex,
  setSelectedImageIndex,
  closeCarousel,
  storeImage,
  showNavigationButton = true,
  enableSwipe = false,
  enableZooming = false,
}: CarouselPropsType) {
  const { t: translate } = useTranslation("page_detail");
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(selectedImageIndex ?? 0);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  // Swipe variables
  const [startX, setStartX] = useState<number | null>(null);
  const [endX, setEndX] = useState<number | null>(null);

  // Close carousel when clicking on escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") closeCarousel();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeCarousel]);

  // Zooming in and out
  function zoomIn() {
    if (zoomLevel < ZOOM_IN_LIMIT) {
      setZoomLevel((prev) => parseFloat((prev + ZOOM_STEP).toFixed(2)));
    }
  }

  function zoomOut() {
    if (zoomLevel > ZOOM_OUT_LIMIT) {
      setZoomLevel((prev) => parseFloat((prev - ZOOM_STEP).toFixed(2)));
    }
  }

  // Manage body overflow on mount and unmount
  useEffect(() => {
    // Hide scrollbar
    document.body.style.overflow = "hidden";

    return () => {
      // Restore scrollbar
      document.body.style.overflow = "auto";
    };
  }, []);

  // Image navigation
  function nextImage() {
    setCurrentImageIndex((prevIndex) => {
      const newIndex = prevIndex === images.length - 1 ? 0 : prevIndex + 1;
      if (setSelectedImageIndex) {
        setSelectedImageIndex(newIndex);
      }
      return newIndex;
    });
  }

  function prevImage() {
    setCurrentImageIndex((prevIndex) => {
      const newIndex = prevIndex === 0 ? images.length - 1 : prevIndex - 1;
      if (setSelectedImageIndex) {
        setSelectedImageIndex(newIndex);
      }
      return newIndex;
    });
  }
  useEffect(() => {
    setCurrentImageIndex(selectedImageIndex ?? 0);
  }, [images, selectedImageIndex]);

  // Touch handlers for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (startX === null || endX === null) return;

    const difference = startX - endX;

    if (difference > THRESHOLD_SWIPE_DISTANCE) {
      nextImage();
    }

    if (difference < -THRESHOLD_SWIPE_DISTANCE) {
      prevImage();
    }

    setStartX(null);
    setEndX(null);
  };

  return (
    <div
      className="fixed inset-0 w-full h-screen bg-darkGrey flex flex-col items-center justify-center z-[1050] px-20 overflow-hidden"
      {...(enableSwipe && {
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
      })}
    >
      <nav className="flex justify-between w-full py-6 px-10 items-center absolute top-0  z-10">
        <div className="flex flex-row gap-5 items-center">
          <button type="button" onClick={closeCarousel}>
            <Image src={closeIcon} alt="Close" width={24} height={24} />
          </button>
          {storeImage && (
            <>
              <StoreLogo
                imageName={getImageName(storeImage)}
                alt={translate("image_not_found")}
                className="rounded-full object-cover border-2 border-orange-300 w-[60px] lg:w-[90px] aspect-square"
              />
            </>
          )}
        </div>
        {enableZooming && (
          <div className="flex flex-row gap-5 items-center">
            <button
              disabled={zoomLevel >= ZOOM_IN_LIMIT}
              onClick={zoomIn}
              className={`${zoomLevel >= ZOOM_IN_LIMIT ? "text-white/80" : "text-white"}`}
            >
              <CiZoomIn
                size={34}
                style={{ color: zoomLevel >= ZOOM_IN_LIMIT ? "gray" : "white", strokeWidth: 0.8 }}
                className="zoom-icon"
              />
            </button>
            <button
              disabled={zoomLevel <= ZOOM_OUT_LIMIT}
              onClick={zoomOut}
              className={`${zoomLevel <= ZOOM_OUT_LIMIT ? "text-white/80" : "text-white"}`}
            >
              <CiZoomOut
                size={34}
                style={{ color: zoomLevel <= ZOOM_OUT_LIMIT ? "gray" : "white", strokeWidth: 0.8 }}
                className="zoom-icon"
              />{" "}
            </button>
          </div>
        )}
      </nav>
      <div className="flex justify-around items-center w-full h-full gap-5 relative">
        {showNavigationButton && (
          <button type="button" onClick={prevImage}>
            <Image
              src={prevIcon}
              alt="Previous"
              width={50}
              height={50}
              className="rtl:rotate-180"
            />
          </button>
        )}

        <div className="w-full h-full flex items-center justify-center">
          <CarouselImage
            key={`${getImageName(images.at(currentImageIndex))}__${zoomLevel}`}
            imageName={getImageName(images.at(currentImageIndex))}
            alt={translate("image_not_found")}
            zoom={zoomLevel}
          />
        </div>
        {showNavigationButton && (
          <button type="button" onClick={nextImage}>
            <Image src={nextIcon} alt="Next" width={50} height={50} className="rtl:rotate-180" />
          </button>
        )}
      </div>
    </div>
  );
}

export default Carousel;
